/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../ExtensionBaseController',
	'./DataSourcesView'
], function(_, BaseController, DataSourcesView) {
	'use strict';

	const SOURCE_TYPE = {
		PACKAGE: 'package'
	};

	class DataSourcesController extends BaseController {

		constructor(dashboard, appSettings, eventRouter){
			super(dashboard, appSettings, eventRouter);
			this.dataSourcesFeature = this.dashboard.getFeature('DataSources');

			this.emptyDatasetRenderer = DataSourcesView.renderEmptyDataset;
			this.MUIDataGridRender = DataSourcesView.renderMUIGrid;

		}

		/**
		 * @see ExtensionBaseController
		 */
		getTabContrib(event, eventName, currentTabId){
			return Promise.all([this._prepareDataSources(), this._getSelectedContent()])
				.then((deps) => {

					let tabContribs = deps[0];
					let content = deps[1];
					if (!tabContribs.length){
						return null;
					}

					let selectedTab = null;
					let activeSourceId = null;
					switch(eventName) {
						case 'dataSourcePanel:dataSourceSelected':
							if(event && event.sender){
								selectedTab = _.findWhere(tabContribs, { id: event.sender });
								selectedTab.isSelectedOnTrayRender = true;
							} else {
								selectedTab = _.findWhere(tabContribs, { id: currentTabId });
								if(selectedTab){
									selectedTab.isSelectedOnTrayRender = true;
								}
							}
							break;
						case 'shapingmodel:changed':
							activeSourceId = this.dashboard.getActiveDataSourceId();
							if(!activeSourceId){
								throw new Error('shapingmodel:changed triggered, but there is no active data source');
							}
							selectedTab = _.findWhere(tabContribs, { id: activeSourceId });
							if(currentTabId === activeSourceId){
								selectedTab.select = this.select.bind(this, { id: activeSourceId, forceRender: true });
								selectedTab.isSelectedOnTrayRender = true;
							}
							break;
						case 'widget:rerendered':
							selectedTab = _.findWhere(tabContribs, { id: currentTabId });
							//Widget rerendered but a datasource view is the current tab. 
							//The data source view should still be the active view
							if(selectedTab){
								selectedTab.isSelectedOnTrayRender = true;
							}
							break;
						case 'splitterPanel:show':
							selectedTab = _.findWhere(tabContribs, { id: currentTabId });
							if(selectedTab){
								selectedTab.isSelectedOnTrayRender = true;
								selectedTab.select = this.select.bind(this, { id: currentTabId, forceRender: true });
							}
							break;
						case 'remove':
							selectedTab = _.findWhere(tabContribs, { id: currentTabId });
							if(selectedTab){
								selectedTab.isSelectedOnTrayRender = true;
								selectedTab.select = this.select.bind(this, { id: currentTabId, forceRender: true });
							}
							break;
					}


					if(!selectedTab && !content){
						let activeSourceId = this.dashboard.getActiveDataSourceId();
						if(!activeSourceId){
							selectedTab = tabContribs[0];
						}else{
							selectedTab = _.findWhere(tabContribs, { id: activeSourceId });
						}
						selectedTab.isSelectedOnTrayRender = true;
					}

					return tabContribs;
				});
		}

		/**
		 * Given an array of source model object, returns and array of source objects that the tab control expects.
		 * @return {Promise} promise that will resolve with an array of source object ready for the tab control
		 */
		_prepareDataSources() {
			const whenNamesReady = [];
			const aPrepDataSources = [];
			const getName = (source, item) => source.getLocalizedName().then(name => {
				item.name = name;
				item.state = source.getState(); //State can be updated by the getLocalizedName() call, so set it after that call has finished.
			}).fail(() => {
				item.state = 'error';
			});
			const dataSources = this.dataSourcesFeature.getDataSourceList() || [];

			dataSources.forEach(dataSource => {
				const id = dataSource.getId();
				const item = {
					type: dataSource.getType(),
					id: id,
					select: this.select.bind(this, { id })
				};
				aPrepDataSources.push(item);
				whenNamesReady.push(getName(dataSource, item));
			});

			return Promise.all(whenNamesReady).then(() => aPrepDataSources);
		}

		/**
		 * Renders the data source grid view.
		 */
		render(context, container){
			const source = this.dataSourcesFeature.getDataSource(context.id);
			if (source && source.getType() === SOURCE_TYPE.PACKAGE) {
				return this.emptyDatasetRenderer(container);
			}

			return source.getModule().then(module => {
				// enableDataQuality is a string
				const enableDataQuality = this.appSettings.options && this.appSettings.options.config ? this.appSettings.options.config.enableDataQuality=='true' : true;
				return this.MUIDataGridRender(module, enableDataQuality, container, this.dashboard);
			});
		}

		/**
		 * @see ExtensionBaseController
		 */
		select(context, currentTabId, container){
			if(currentTabId === context.id && !context.forceRender){
				return Promise.resolve(false);
			}

			this.dataSourcesFeature.setActiveDataSourceId(context.id);
			const dataSource = this.dataSourcesFeature.getDataSource(context.id);
			this.eventRouter.trigger('dataSourceGrid:dataSourceSelected', dataSource);

			return this.render(context, container);
		}
	}

	return DataSourcesController;
});